var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class Blustream {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.framework = framework;
        // other class properties and constructor
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: 'STATUS',
                next: /==========/,
            },
        ];
        // Product-specific information for parsing the STATUS response.
        this.collectingStatusResponses = false;
        this.products = [
            {
                firmwareRegExp: /FW Version:\s+([\d+\.]*)/i,
                hasVolume: false,
                hasVolumeMute: false,
                mode: 'MATRIX',
                model: 'C44-KIT',
                modelNumber: '-MRUxQCH-6GynWFmfoaL',
                output: {
                    labelRegExp: /Out(?:put)*\s+(?:FromIn|SelectInput)/i,
                    valueRegExp: /(\d+)\s+(\d+)\s+/i,
                },
                power: {
                    labelRegExp: /^Power\s+IR/i,
                    valueRegExp: /^(On|Off)\s+/i,
                },
            },
            {
                firmwareRegExp: /FW Version:\s+([\d+\.]*)/i,
                hasVolume: true,
                hasVolumeMute: true,
                inputLookup: ['HDMI1', 'HDMI2', 'HDMI3', 'DP', 'USB-C', 'VGA'],
                model: 'MFP62',
                modelNumber: '-M5g_w8h5S1qi0ymg0T5',
                output: {
                    labelRegExp: /Out(?:put)*\s+(?:FromIn|SelectInput)/i,
                    valueRegExp: /(\d+)\s+(\S+)\s+/i,
                },
                power: {
                    labelRegExp: /^Power\s+IR/i,
                    valueRegExp: /^(On|Off)\s+/i,
                },
                volume: {
                    labelRegExp: /SCA\s+Vol\s+Mute/i,
                    valueRegExp: /(?:ORG|EMB)\s+(\d+)\s+(On|Off)/i,
                    min: 50,
                    max: 100,
                },
            },
            {
                firmwareRegExp: /FW Version:\s+([\d+\.]*)/i,
                hasVolume: true,
                hasVolumeMute: true,
                mode: 'MATRIX',
                model: 'MFP72',
                modelNumber: '-M5g_t8MFcK8_22gnqqY',
                output: {
                    labelRegExp: /Out(?:put)*\s+(?:FromIn|SelectInput)/i,
                    valueRegExp: /(\d+)\s+(\S+)\s+(?:Yes|No)\s+(?:Yes|No)\s+(?:Matrix|Splitter)/i,
                },
                power: {
                    labelRegExp: /^Power\s+IR/i,
                    valueRegExp: /^(On|Off)\s+/i,
                },
                volume: {
                    labelRegExp: /ScalerAudio\s+Volume\s+Mute/i,
                    valueRegExp: /(?:Ori?ginal|Embed\sL\/R)\s+(\d+)\s+(On|Off)/i,
                    min: 0,
                    max: 30,
                },
            },
            {
                firmwareRegExp: /FW Version:\s+([\d+\.]*)/i,
                hasVolume: true,
                hasVolumeMute: false,
                model: 'MFP112',
                modelNumber: '-M5g_yvYs8mauLxVU-fv',
                output: {
                    labelRegExp: /Out(?:put)*\s+(?:FromIn|SelectInput)/i,
                    valueRegExp: /^(\d+)\s+(\S+)\s+(?:Yes|No)\s+(?:Yes|No)\s+(?:Matrix|Splitter)/i,
                },
                power: {
                    labelRegExp: /^Power\s+IR/i,
                    valueRegExp: /^(On|Off)\s+/i,
                },
                volume: {
                    labelRegExp: /ScalerAudio\s+Volume\s+/i,
                    valueRegExp: /(?:Ori?ginal|Embed\sL\/R)\s+(\d+)\s+/i,
                    min: 0,
                    max: 30,
                },
            },
        ];
        this.product = this.products[1]; // MFP72
        // Async handler for status responses, which are queried in onConfigure. Status responses start
        // and end with a "=========" line.
        this.statusAsyncHandler = {
            name: 'statusResponse',
            regExp: /^(?!(?:MFP\d+\>\s*)?\[SUCCESS\])([\s\S]*)/i,
            callback: (match) => {
                if (!this.collectingStatusResponses && match[1].startsWith('=====')) {
                    this.collectingStatusResponses = true;
                }
                else if (this.collectingStatusResponses && match[1].startsWith('=====')) {
                    this.collectingStatusResponses = false;
                    this.processStatus(this.statusResponseArray);
                }
                else if (this.collectingStatusResponses && match[1].length > 0) {
                    this.statusResponseArray.push(match[1]);
                }
            },
        };
        this.statusResponseArray = [];
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(power ? 'PON' : 'POFF', /P(ON|OFF)|\[ERROR\]/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), power ? 19000 : 4000);
                this.framework.suspend(power ? 19000 : 2000);
                return match[1] ? match[1] === 'ON' : this.state.power.value;
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`OUT${key}FR${value}`, /(?:set all output connect from input (\d+))|(?:set output\s(?:\d+) connect\sfrom\s(?:input\s)?([^\.]+)\.?)|\[ERROR\]/i, tags);
                if (match[1]) {
                    return this.simplifyInputName(match[1]).toUpperCase();
                }
                else if (match[2]) {
                    return this.simplifyInputName(match[2]).toUpperCase();
                }
                else {
                    return this.state.videoMatrixRoutes.values[key];
                }
            }),
            asyncHandlers: {
                // MFP62 sends commands on button presses
                regExp: /\[SUCCESS\](?:set all output connect from input (\d+))|(?:set.+output\s(\d+) connect\sfrom\s(?:input\s)?([^\.]+)\.?)/i,
                callback: (match) => {
                    if (match[1]) {
                        this.state.videoMatrixRoutes.update('01', this.simplifyInputName(match[1]).toUpperCase());
                    }
                    else if (match[3]) {
                        this.state.videoMatrixRoutes.update(match[2], this.simplifyInputName(match[3]).toUpperCase());
                    }
                },
            },
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.volume.value;
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`VOL ${volume.toString(10)}`, /Set .+ volume to (-?\d+)|\[ERROR\]/i, tags);
                return match[1] === undefined ? this.state.volume.value : parseInt(match[1], 10);
            }),
            asyncHandlers: {
                regExp: /\[SUCCESS\]Set[\s\S]+volume to\s+(-?\d+)/i,
                callback: (match) => {
                    this.state.volume.update(parseInt(match[1], 10));
                },
            },
            range: (config) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                return {
                    max: (_b = (_a = this.product.volume) === null || _a === void 0 ? void 0 : _a.max) !== null && _b !== void 0 ? _b : 0,
                    min: (_d = (_c = this.product.volume) === null || _c === void 0 ? void 0 : _c.min) !== null && _d !== void 0 ? _d : 0,
                    round: 1,
                };
            }),
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                var _e;
                const product = this.products.find(p => { var _a; return p.modelNumber === ((_a = config === null || config === void 0 ? void 0 : config.modelNumber) !== null && _a !== void 0 ? _a : ''); });
                return product ? (_e = product.hasVolume) !== null && _e !== void 0 ? _e : false : false;
            }),
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.volumeMute.value;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`MUTE${mute ? 'ON' : 'OFF'}`, /Set [\s\S]+audio mute\s+(OFF|ON)|\[ERROR\]/i, tags);
                return match[1] ? match[1] === 'ON' : this.state.volumeMute.value;
            }),
            asyncHandlers: {
                regExp: /\[SUCCESS\]Set[\s\S]+audio\s+mute\s+(OFF|ON)/i,
                callback: (match) => {
                    this.state.volumeMute.update(match[1] === 'ON');
                },
            },
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                var _f;
                const product = this.products.find(p => { var _a; return p.modelNumber === ((_a = config === null || config === void 0 ? void 0 : config.modelNumber) !== null && _a !== void 0 ? _a : ''); });
                return product ? (_f = product.hasVolumeMute) !== null && _f !== void 0 ? _f : false : false;
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () { return yield this.queryStatus(true); }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            const product = this.products.find(p => { var _a; return p.modelNumber === ((_a = config === null || config === void 0 ? void 0 : config.modelNumber) !== null && _a !== void 0 ? _a : ''); });
            this.product = product ? product : this.products[0];
            if (this.statusAsyncHandlerId === undefined) {
                this.statusAsyncHandlerId = this.socket.registerAsync(this.statusAsyncHandler);
            }
            yield this.queryStatus();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            if (this.statusAsyncHandlerId !== undefined) {
                this.socket.unregisterAsync(this.statusAsyncHandlerId);
                this.statusAsyncHandlerId = undefined;
            }
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
        });
    }
    // other class methods
    queryStatus(isPoll = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const tags = isPoll ? ['poll'] : [];
            this.statusResponseArray = [];
            this.collectingStatusResponses = false;
            yield this.socket.send('STATUS', /STATUS$/, tags);
        });
    }
    processStatus(data) {
        var _a, _b, _c, _d;
        let match;
        // Iterate through status response looking for a label match, then process the value from
        // the next line.
        //
        for (let i = 0; i < data.length; i++) {
            if ((match = this.product.firmwareRegExp.exec(data[i]))) {
                this.state.firmwareVersion.update(match[1]);
            }
            else if ((match = this.product.power.labelRegExp.exec(data[i]))) {
                if ((match = this.product.power.valueRegExp.exec(data[i + 1]))) {
                    this.state.power.update(match[1].toUpperCase() === 'ON' ? true : false);
                }
            }
            else if ((match = this.product.output.labelRegExp.exec(data[i]))) {
                let outputIndex = 1;
                while ((match = this.product.output.valueRegExp.exec(data[i + outputIndex++]))) {
                    if (match) {
                        const inputName = match[2];
                        // The MFP62 requires translation from the input name to the codes defined
                        // in equipmentTypes.
                        if (this.product.model === 'MFP62' && this.product.inputLookup) {
                            const index = this.product.inputLookup.findIndex(p => p === inputName);
                            const indexStr = index > -1 ? (index + 1).toString() : '1';
                            const inputCode = indexStr.padStart(2, '0');
                            this.state.videoMatrixRoutes.update(match[1], inputCode);
                        }
                        else {
                            this.state.videoMatrixRoutes.update(match[1], match[2].toUpperCase());
                        }
                    }
                }
            }
            else if ((match = (_b = (_a = this.product.volume) === null || _a === void 0 ? void 0 : _a.labelRegExp.exec(data[i])) !== null && _b !== void 0 ? _b : null)) {
                if ((match = (_d = (_c = this.product.volume) === null || _c === void 0 ? void 0 : _c.valueRegExp.exec(data[i + 1])) !== null && _d !== void 0 ? _d : null)) {
                    if (this.product.hasVolume)
                        this.state.volume.update(parseInt(match[1], 10));
                    if (this.product.hasVolumeMute)
                        this.state.volumeMute.update(match[2] === 'On');
                }
            }
        }
    }
    // Convert "HDMI 01" -> "HDMI1" and "VGA 4" -> "VGA4".
    simplifyInputName(value) {
        const hdmiRegExp = /(HDMI|VGA)\s0?(\d+)/i;
        const match = hdmiRegExp.exec(value);
        if (match)
            return `${match[1]}${match[2]}`;
        return value;
    }
}
// static properties
Blustream.driverType = 'socketDriver';
Blustream.key = '-M5g_hap5PUG_lHcCUoF';
Blustream.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r', '\n'],
    },
    sendTerminator: '\r',
};
Blustream.socketOptions = [
    Object.assign(Object.assign({}, Blustream.baseSocketOptions), { port: 8000 }),
    Object.assign(Object.assign({}, Blustream.baseSocketOptions), { port: 4999 }),
];
